import React, { useState } from 'react';
import StatusBadge from '../components/redesign/StatusBadge';
import { EntityChip } from '../components/redesign/EntityChip';
import { Button, Card } from '../components/redesign/ui';
import { STATUS_DOT } from '../lib/statusConfig';
import { CaretRight, WarningCircle, Check } from '@phosphor-icons/react';
import {
  RESOLUTION_TAKEN_OPTIONS,
  RESPONSIBILITY_ATTRIBUTION_OPTIONS,
  RESPONSIBILITY_ATTRIBUTION_LABEL,
  FINANCE_GATE_GROUPS,
  ICARRY_APPLICABLE_GROUPS,
  KUWAIT_AREAS,
  ORDER_SOURCE_OPTIONS,
  CUSTOMER_SEGMENT_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  CANCELLATION_REASON_OPTIONS,
  DEPARTMENT_OPTIONS,
  COMPLAINT_GROUP_LABEL,
  DEPT_OPTIONS_BY_ENTITY,
  SKU_OPTIONS,
  SKU_IS_PRIVATE_LABEL,
  SKU_APPLICABLE_GROUPS,
  QUANTITY_UNIT_OPTIONS,
  QUANTITY_UNIT_LABEL,
  SAMPLE_STAGES,
  SAMPLE_STAGE_BY_SLUG,
  SAMPLE_APPLICABLE_GROUPS,
  slugsForDepartment,
} from '../constants/ticketOptions';
import { formatDistanceToNowStrict } from 'date-fns';
import { STATUS_STEPS, STATUS_FLOW, STATUS_LABELS } from '../utils/constants';

// ── shared token-styled control classes ────────────────────────────────────
const INPUT = 'w-full rounded-field border border-paper-border2 bg-paper px-3 py-2 text-sm text-ink outline-none focus:border-ink';
const LABEL = 'mb-1 block text-xs text-ink-faint';
const HEADING = 'font-serif text-base text-ink';
const SAVE_BTN = 'rounded-field bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink/90 disabled:opacity-50';
const CANCEL_BTN = 'rounded-field border border-paper-border2 px-3 py-1.5 text-xs text-ink-muted hover:bg-paper-muted';

const PLATFORM_ORDER_SOURCES = ['Talabat', 'Zyda', 'Sheeel', 'Snoonu', 'Mashareeb', 'Bylbait', 'One Global', 'Cari'];

// Sales-call outcomes → target status (ported verbatim from live TicketDetail.js).
const SALES_OUTCOMES = [
  { key: 'interested',          label: '✅ Interested',          status: 'in_progress' },
  { key: 'scheduled_visit',     label: '📅 Scheduled Visit',     status: 'in_progress' },
  { key: 'order_placed',        label: '🛒 Order Placed',        status: 'resolved' },
  { key: 'existing_customer',   label: '📦 Existing Customer',   status: 'in_progress' },
  { key: 'mosque_inquiry',      label: '🕌 Mosque Inquiry',      status: 'pending_customer_contact' },
  { key: 'sponsorship_request', label: '🤝 Sponsorship Request', status: 'pending_customer_contact' },
  { key: 'general_inquiry',     label: '❓ General Inquiry',     status: 'pending_customer_contact' },
  { key: 'follow_up',           label: '🔄 Follow Up Needed',    status: 'pending_customer_contact' },
  { key: 'not_interested',      label: '❌ Not Interested',      status: 'resolved' },
  { key: 'no_answer',           label: '📵 No Answer',           status: 'pending_customer_contact' },
  { key: 'wrong_number',        label: '🔁 Wrong Number',        status: 'resolved' },
];

const deptLabel = (slug) => slug
  ? slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  : null;

// 3-button segmented control for responsibility_attribution — mirrors the
// SalesOutcome styling (below) so the resolve modal reads consistently.
function ResponsibilityButtons({ value, onChange, disabled = false }) {
  return (
    <div className="grid grid-cols-3 gap-2.5">
      {RESPONSIBILITY_ATTRIBUTION_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={`rounded-field px-2.5 py-2.5 text-center text-[13px] transition ${
            value === o.value
              ? 'border-[1.5px] border-status-resolved bg-status-resolved/8 font-semibold text-status-resolved'
              : 'border border-paper-border2 text-ink hover:bg-paper-muted'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
const getStatusLabel = (status) => STATUS_LABELS[status] || status?.replace(/_/g, ' ') || '—';
const fmtDate = (v) => { try { return v ? new Date(v).toLocaleString() : '—'; } catch { return '—'; } };
const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// Small label/value cell.
const F = ({ label, children, dir }) => (
  <div>
    <div className="mb-0.5 text-xs text-ink-faint">{label}</div>
    <div className="text-sm text-ink" dir={dir}>{children}</div>
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3"><span className="text-ink-faint">{k}</span><span className="text-ink" dir="auto">{v}</span></div>
);

// Neutral display chip — used to render each item of a multi-value field.
// Visual weight tracks EntityChip's shape (rounded-chip) but strips the icon
// and color family so it reads as "one of several tags" rather than a status.
const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-chip bg-paper-muted px-2 py-0.5 text-xs text-ink">
    {children}
  </span>
);

// ── generic editable field card (§3 item 8) ────────────────────────────────
// kind: 'select' | 'text' | 'multiselect'. Calls onSave(value) → container
// onFieldEdit(field, value). For multiselect the value is a comma+space joined
// string in canonical (options-array) order — call sites stay string-only.
function EditField({
  title, field, value, displayValue, canEdit, onSave, kind = 'text', options = [],
  placeholder, addLabel, emptyRender, children,
}) {
  const isMulti = kind === 'multiselect';
  const parseMulti = (v) => (v ? String(v).split(', ').filter(Boolean) : []);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(isMulti ? parseMulti(value) : (value || ''));
  const begin = () => {
    setDraft(isMulti ? parseMulti(value) : (value || ''));
    setEditing(true);
  };
  const save = async () => {
    if (isMulti) {
      // Legacy values in draft that no longer exist in options would be silently
      // stripped by the options.filter below — they have no checkbox so the user
      // can neither preserve nor remove them deliberately. Confirm the drop so
      // it's not silent. Common case: an option was retired (e.g. product_type
      // '1L' dropped 2026-09-13) and a legacy ticket still carries it.
      const dropped = draft.filter((v) => !options.includes(v));
      if (dropped.length > 0) {
        // eslint-disable-next-line no-alert
        const ok = window.confirm(
          `This ticket has a Product Type value that is no longer selectable and will be removed if you save:\n\n  ${dropped.join('\n  ')}\n\nSave anyway?`
        );
        if (!ok) return; // stay in edit mode; outer Cancel abandons without saving
      }
    }
    const out = isMulti ? options.filter((o) => draft.includes(o)).join(', ') : draft;
    await onSave(out);
    setEditing(false);
  };
  const shownValue = displayValue !== undefined ? displayValue : value;
  const editable = value || displayValue;
  const readChips = isMulti ? parseMulti(value) : null;
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-ink">{title}</span>
        {canEdit && !editing && (
          <button onClick={begin} className="text-xs text-status-under-process hover:underline">
            {editable ? 'Edit' : (addLabel || '+ Add')}
          </button>
        )}
      </div>
      {editing ? (
        isMulti ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1.5">
              {options.map((o) => (
                <label key={o} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={draft.includes(o)}
                    onChange={(e) => setDraft((prev) => e.target.checked ? [...prev, o] : prev.filter((x) => x !== o))} />
                  {o}
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={save} className={SAVE_BTN}>Save</button>
              <button onClick={() => setEditing(false)} className={CANCEL_BTN}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            {children ? children({ draft, setDraft }) : kind === 'select' ? (
              <select value={draft} onChange={(e) => setDraft(e.target.value)} className={INPUT}>
                <option value="">— None —</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input type="text" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} className={INPUT} />
            )}
            <button onClick={save} className={SAVE_BTN}>Save</button>
            <button onClick={() => setEditing(false)} className={CANCEL_BTN}>Cancel</button>
          </div>
        )
      ) : readChips && readChips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {readChips.map((v, i) => <Chip key={i}>{v}</Chip>)}
        </div>
      ) : shownValue ? (
        <span className="text-sm text-ink" dir="auto">{shownValue}</span>
      ) : (emptyRender || <span className="text-sm italic text-ink-faint">Not set</span>)}
    </Card>
  );
}

// Sample chain-of-custody stepper for Abraaj quality complaints.
// Two pips: Logistics → Quality Control. Each pip shows its timestamp when
// stamped. Advance affordance visible only to the department that owns the
// target stage (+ super_admin/entity_manager). If the ticket is at
// 'quality-control' but Logistics was never marked, the Logistics pip is
// greyed with a subtle warning (skipped stage). Backward correction via the
// "Reset" affordance shown to the owner of the current stage.
function SampleStageStepper({ ticket, currentUser, onAdvance, saving }) {
  const stage = ticket.sample_stage || null;
  const timestamps = SAMPLE_STAGES.reduce((acc, s) => {
    acc[s.slug] = ticket[s.timestampField];
    return acc;
  }, {});
  const stageOrder = SAMPLE_STAGES.map((s) => s.slug);
  const currentIndex = stage ? stageOrder.indexOf(stage) : -1;

  const isPrivileged = ['super_admin', 'entity_manager'].includes(currentUser?.role);
  const callerFamily = slugsForDepartment(currentUser?.department);
  // Multi-owner stages (e.g. warehouse): ANY caller in the owner array can
  // advance. Uniform array-of-slugs shape; mirrors backend STAGE_OWNERS.
  const ownsStage = (stageSlug) => {
    const def = SAMPLE_STAGE_BY_SLUG[stageSlug];
    if (!def) return false;
    return (def.ownerDeptSlugs || []).some((s) => callerFamily.includes(s));
  };
  const canAdvanceTo = (targetSlug) => isPrivileged || ownsStage(targetSlug);
  const canReset = stage && (isPrivileged || ownsStage(stage));

  // Skipped-stage detection: any earlier stage lacking a timestamp when we
  // reached a later one. Reaching 'warehouse' with no logistics timestamp,
  // or 'quality-control' with either earlier one missing, greys the missing
  // pip and shows the warning.
  const isStageSkipped = (stageSlug) => {
    if (currentIndex < 0) return false;
    const stageIdx = stageOrder.indexOf(stageSlug);
    return stageIdx >= 0 && stageIdx < currentIndex && !timestamps[stageSlug];
  };
  const fmt = (ts) => ts ? new Date(ts).toLocaleString() : null;

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold text-ink">Sample Chain of Custody</span>
        {canReset && (
          <button onClick={() => onAdvance('none')} disabled={saving}
            className="text-xs text-ink-faint hover:text-ink-muted hover:underline disabled:opacity-50">
            Reset
          </button>
        )}
      </div>
      <div className="mb-4 flex gap-1.5">
        {SAMPLE_STAGES.map((s, i) => {
          const isActive = stage === s.slug;
          const isCompleted = currentIndex > i;
          const isSkipped = isStageSkipped(s.slug);
          const bg = isSkipped ? 'bg-status-pending-finance/30'
                   : isActive ? 'bg-status-pending-finance'
                   : isCompleted ? 'bg-status-resolved'
                   : 'bg-paper-border2';
          return <div key={s.slug} title={s.label} className={`h-1.5 flex-1 rounded-sm ${bg}`} />;
        })}
      </div>
      <div className="space-y-2.5">
        {SAMPLE_STAGES.map((s) => {
          const ts = timestamps[s.slug];
          const isActive = stage === s.slug;
          const isSkipped = isStageSkipped(s.slug);
          const showAdvance = !isActive && canAdvanceTo(s.slug);
          const advanceLabel = s.slug === 'logistics'       ? 'Mark with Logistics'
                             : s.slug === 'warehouse'       ? 'Mark at Warehouse'
                             : s.slug === 'quality-control' ? 'Mark Received at Lab'
                             : `Mark ${s.shortLabel}`;
          return (
            <div key={s.slug} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className={`text-sm ${isActive ? 'font-semibold text-ink' : 'text-ink-muted'}`}>{s.label}</div>
                {ts ? (
                  <div className="text-[11px] text-ink-faint">Marked {fmt(ts)}</div>
                ) : isSkipped ? (
                  <div className="text-[11px] text-status-pending-finance">⚠ Stage skipped — never marked</div>
                ) : (
                  <div className="text-[11px] text-ink-faint">Not yet</div>
                )}
              </div>
              {showAdvance && (
                <button onClick={() => onAdvance(s.slug)} disabled={saving}
                  className="rounded-field bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink/90 disabled:opacity-50">
                  {advanceLabel}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function TicketDetailView({
  ticket,
  notes = [], statusHistory = [], deptActions = [], thirdPartySubmissions = [], attachments = [],
  hubspotEnrichment, enrichmentLoading,
  assignableUsers = [], canReassign = false,
  canEditAttribution = false,
  canEdit, canManageTicket, isDeptUser, canConfirmFinance, canRespondAsDept = false,
  resolving, financeConfirming, closing, reopening, escalating, savingEdit,
  addingNote, submittingDeptResponse, uploadingAttachment,
  onSaveEdit, onAddNote, onResolve, onMoveToPendingFinance, onFinanceConfirm,
  onClose, onReopen, onEscalate, onSubmitDeptResponse, onSalesOutcome, onAttributionEdit,
  onFieldEdit, onUploadAttachment, onDownloadAttachment,
  advancingSampleStage = false, currentUser, onAdvanceSampleStage,
  AuthThumb,
}) {
  // ── local UI/form state (presentation-owned) ──
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [noteText, setNoteText] = useState('');
  const [closureForm, setClosureForm] = useState({ customer_contacted: false, contact_method: 'WhatsApp', closing_notes: '' });
  const [reopenReason, setReopenReason] = useState('');
  const [escalateForm, setEscalateForm] = useState({ department: '', reason: '' });
  const [resolveForm, setResolveForm] = useState({
    resolution_taken: '',
    responsibility_attribution: '',
    notes: '',
  });
  const [editingAttribution, setEditingAttribution] = useState(false);
  const [attributionDraft, setAttributionDraft] = useState('');
  const [deptResponse, setDeptResponse] = useState({ response: '', action_taken: '', confirmed_done: false });
  const [deptFiles, setDeptFiles] = useState([]);
  const [salesOutcome, setSalesOutcome] = useState(null);
  const [salesNotes, setSalesNotes] = useState('');
  const [areaSearch, setAreaSearch] = useState('');

  if (!ticket) return null;

  const isAbraaj = ticket.entity_slug === 'abraaj-water';
  const group = ticket.complaint_group;

  // Progress bar visited-set logic (ported from live).
  const visited = new Set((statusHistory || []).map((h) => h.new_status).filter(Boolean));
  visited.add('new');
  visited.add(ticket.status);
  const stepStatuses = (step) => step.statuses || [step.key];
  const currentStepIndex = STATUS_STEPS.findIndex((s) => stepStatuses(s).includes(ticket.status));
  const isStepCompleted = (step, i) => i < currentStepIndex && stepStatuses(step).some((st) => visited.has(st));
  const isStepActive = (step) => stepStatuses(step).includes(ticket.status);

  const beginEdit = () => {
    setEditForm({
      status: ticket.status,
      complaint_priority: ticket.complaint_priority,
      refund_amount: ticket.refund_amount || '',
      refund_auth_code: ticket.refund_auth_code || '',
    });
    setEditing(true);
  };
  const saveEdit = async () => { await onSaveEdit(editForm); setEditing(false); };

  const submitSales = async () => {
    if (!salesOutcome) return;
    const o = SALES_OUTCOMES.find((x) => x.key === salesOutcome);
    await onSalesOutcome({ outcome: o, note: salesNotes, target_status: o.status });
    setSalesOutcome(null); setSalesNotes('');
  };

  const submitDept = async () => {
    await onSubmitDeptResponse({ ...deptResponse, files: deptFiles });
    setDeptResponse({ response: '', action_taken: '', confirmed_done: false });
    setDeptFiles([]);
  };

  const deptOptions = DEPT_OPTIONS_BY_ENTITY[ticket.entity_slug] || [];

  // Rows for the Ticket Information card (label, value) — conditional entries preserved.
  const infoRows = [
    ['Entity', ticket.entity_name],
    ['Complaint Group', COMPLAINT_GROUP_LABEL[group] || group],
    ['Complaint Type', ticket.complaint_type],
    ['Subtype', ticket.complaint_subtype],
    ['Channel', ticket.channel_source],
    ['SAP Order Ref', ticket.sap_order_reference],
    ['Meal Date', ticket.meal_date ? fmtDate(ticket.meal_date) : null],
    ['Created By', ticket.created_by_label || ticket.created_by_name],
    ['Created', ticket.created_at ? fmtDate(ticket.created_at) : null],
    ...(isAbraaj ? [['Department Assigned', ticket.department_assigned]] : []),
    ...(ticket.resolution_taken ? [['Resolution Taken', ticket.resolution_taken]] : []),
    ...(ticket.order_source ? [['Order Source', ticket.order_source]] : []),
    ...(ticket.customer_segment ? [['Customer Segment', ticket.customer_segment]] : []),
    // Product Type is shown via the multi-value EditField chip card below
    // (line ~404) — omitted from the compact info-rows list to avoid rendering
    // it twice with different formats.
    ...(isAbraaj && ticket.customer_area ? [['Customer Area', ticket.customer_area]] : []),
    ...(isAbraaj && ticket.sap_route ? [['SAP Route', ticket.sap_route]] : []),
    ...(isAbraaj && ticket.product_sku ? [['Product SKU', ticket.product_sku]] : []),
    ...(isAbraaj && ticket.product_sku_brand ? [['Brand / Client', ticket.product_sku_brand]] : []),
    ...(isAbraaj && ticket.product_quantity != null ? [['Quantity', `${ticket.product_quantity}${ticket.product_quantity_unit ? ' ' + (QUANTITY_UNIT_LABEL[ticket.product_quantity_unit] || ticket.product_quantity_unit) : ''}`]] : []),
    ...(isAbraaj && SAMPLE_APPLICABLE_GROUPS.includes(group) && ticket.sample_stage && SAMPLE_STAGE_BY_SLUG[ticket.sample_stage] ? (() => {
        const def = SAMPLE_STAGE_BY_SLUG[ticket.sample_stage];
        const ts = ticket[def.timestampField];
        const rel = ts ? formatDistanceToNowStrict(new Date(ts), { addSuffix: true }) : null;
        return [['Sample', def.label + (rel ? ` · marked ${rel}` : '')]];
      })() : []),
    ...(ticket.platform_order_reference ? [['Platform Order Ref', ticket.platform_order_reference]] : []),
    ...(isAbraaj && ticket.replacement_order_reference ? [['Replacement Order', ticket.replacement_order_reference]] : []),
    ...(group === 'cancel_order' && ticket.cancellation_reason ? [['Cancellation Reason', ticket.cancellation_reason]] : []),
    ...(isAbraaj && ICARRY_APPLICABLE_GROUPS.includes(group) && ticket.icarry_reference ? [['iCarry Reference', ticket.icarry_reference]] : []),
  ].filter(([, v]) => v);

  const rtl = ticket.original_message && /[؀-ۿ]/.test(ticket.original_message);
  const rawContent = (hubspotEnrichment?.message || '').trim();
  const storedDesc = (ticket.complaint_description || '').trim();
  const showFallback = !ticket.original_message && rawContent && rawContent !== storedDesc;

  // Resolve & Finance gate sub-state selector (§3 item 11).
  const showResolvePanel = canManageTicket && !['closed', 'resolved'].includes(ticket.status);
  const isFinanceGroup = FINANCE_GATE_GROUPS.includes(group);
  const isPendingFinance = ticket.status === 'pending_finance';

  return (
    <div className="p-1">
      <div className="overflow-hidden rounded-card border border-paper-border bg-paper">
        {/* ── header ── */}
        <div className="border-b border-paper-border bg-paper-panel px-7 py-6">
          <div className="mb-3 flex items-center gap-2 text-[13px] text-ink-faint">
            <span>Tickets</span><CaretRight size={12} /><span className="text-ink-muted">{ticket.ticket_number}</span>
          </div>
          <div className="flex items-start justify-between gap-5">
            <div className="flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-3">
                <span className="text-[15px] font-medium text-ink-muted">{ticket.ticket_number}</span>
                <StatusBadge status={ticket.status} />
                {ticket.entity_slug && <EntityChip entitySlug={ticket.entity_slug} />}
              </div>
              <h1 className="mb-2 font-serif text-2xl leading-snug text-ink" dir="auto">{ticket.subject}</h1>
              {/* status banners */}
              {ticket.escalated_to_dept && !['pending_customer_contact', 'closed', 'resolved'].includes(ticket.status) && (
                <div className="text-[13px] text-[#854F0B]">🏷 Routed to: {deptLabel(ticket.escalated_to_dept)}</div>
              )}
              {ticket.status === 'pending_customer_contact' && (
                <div className="text-[13px] text-status-resolved">✅ Dept. confirmed — contact customer to close</div>
              )}
              {ticket.status === 'closed' && ticket.customer_contacted && (
                <div className="text-[13px] text-status-resolved">✅ Closed — customer contacted via {ticket.contact_method}</div>
              )}
              {ticket.status === 'closed' && !ticket.customer_contacted && (
                <div className="text-[13px] text-ink-muted">Closed</div>
              )}
            </div>
            {canEdit && !editing && <Button variant="secondary" onClick={beginEdit}>Edit</Button>}
            {editing && (
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
                <Button variant="primary" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save Changes'}</Button>
              </div>
            )}
          </div>
        </div>

        {/* ── progress bar ── */}
        <div className="flex items-center gap-2 border-b border-paper-border bg-paper-panel px-7 py-4">
          <span className="mr-2 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">Progress</span>
          <div className="flex flex-1 gap-1.5">
            {STATUS_STEPS.map((step, i) => {
              const completed = isStepCompleted(step, i);
              const active = isStepActive(step);
              return (
                <div key={step.key} title={step.label}
                  className={`h-1.5 flex-1 rounded-sm ${active ? 'bg-status-pending-finance' : completed ? 'bg-status-resolved' : 'bg-paper-border2'}`} />
              );
            })}
          </div>
          <span className="ml-2 text-xs text-ink-muted">
            {currentStepIndex >= 0 ? `Step ${currentStepIndex + 1} / ${STATUS_STEPS.length} · ${STATUS_STEPS[currentStepIndex].label}` : getStatusLabel(ticket.status)}
          </span>
        </div>

        {/* ── body: two columns ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr]">
          {/* LEFT column */}
          <div className="flex flex-col gap-5 border-r border-paper-border p-7">
            {/* Ticket Information */}
            <Card className="p-5">
              <div className={`mb-4 ${HEADING}`}>Ticket Information</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3.5">
                {infoRows.map(([label, val]) => <F key={label} label={label} dir="auto">{val}</F>)}
              </div>

              {/* Original Message (stored, RTL-aware) */}
              {ticket.original_message && (
                <div className="mt-4 border-t border-paper-border pt-4">
                  <div className="mb-1.5 text-xs text-ink-faint">Original Message (as received)</div>
                  <div className={`rounded-field border border-paper-border bg-paper p-3.5 text-sm leading-relaxed text-ink-muted ${rtl ? 'text-right' : ''}`} dir={rtl ? 'rtl' : 'auto'}>
                    {ticket.original_message}
                  </div>
                </div>
              )}

              {/* Description */}
              {ticket.complaint_description && (
                <div className="mt-4 border-t border-paper-border pt-4">
                  <div className="mb-1.5 text-xs text-ink-faint">Description</div>
                  <div className="rounded-field border border-paper-border bg-paper p-3.5 text-sm leading-relaxed text-ink-muted" dir="auto">{ticket.complaint_description}</div>
                </div>
              )}

              {/* HubSpot enrichment fallback + skeleton */}
              {!ticket.original_message && (enrichmentLoading || showFallback) && (
                <div className="mt-4 border-t border-paper-border pt-4">
                  <div className="mb-1.5 text-xs text-ink-faint">Original Customer Message (as received)</div>
                  {enrichmentLoading && !rawContent ? (
                    <div className="space-y-1.5">
                      <div className="h-3 w-full animate-pulse rounded bg-paper-muted" />
                      <div className="h-3 w-4/5 animate-pulse rounded bg-paper-muted" />
                      <div className="h-3 w-2/3 animate-pulse rounded bg-paper-muted" />
                    </div>
                  ) : showFallback ? (
                    <div className="rounded-field border border-paper-border bg-paper p-3.5 text-sm leading-relaxed text-ink-muted" dir="auto">{hubspotEnrichment.message}</div>
                  ) : null}
                </div>
              )}

              {/* complaint_photo */}
              {ticket.complaint_photo && (
                <div className="mt-4 border-t border-paper-border pt-4">
                  <div className="mb-1.5 text-xs text-ink-faint">Attached Photo</div>
                  <a href={ticket.complaint_photo?.startsWith('http') ? ticket.complaint_photo.replace(/^https?:\/\/[^/]+/, '') : ticket.complaint_photo}
                    target="_blank" rel="noreferrer" className="text-sm text-status-under-process hover:underline">View attachment ↗</a>
                </div>
              )}

              {/* Department attachments (images array) */}
              {ticket.images && ticket.images.length > 0 && (
                <div className="mt-4 border-t border-paper-border pt-4">
                  <div className="mb-2 text-xs text-ink-faint">Department Attachments ({ticket.images.length})</div>
                  <div className="flex flex-wrap gap-2">
                    {ticket.images.map((img, i) => (
                      <a key={i} href={`/uploads/${img.filename}`} target="_blank" rel="noopener noreferrer"
                        className="rounded-field border border-paper-border2 bg-paper-muted px-2 py-1 text-xs text-status-under-process hover:underline">↗ {img.original_name}</a>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Attachments section (auth thumbnails, upload, download) */}
            <Card className="p-5">
              <div className="mb-3 flex items-center justify-between">
                <span className={HEADING}>Attachments{attachments.length ? ` (${attachments.length})` : ''}</span>
                <label className={`cursor-pointer rounded-field border border-paper-border2 px-3 py-1.5 text-xs ${uploadingAttachment ? 'text-ink-faint' : 'text-status-under-process hover:bg-paper-muted'}`}>
                  {uploadingAttachment ? 'Uploading…' : '+ Upload'}
                  <input type="file" multiple className="hidden" disabled={uploadingAttachment}
                    onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ''; if (f.length) onUploadAttachment(f); }} />
                </label>
              </div>
              {attachments.length === 0 ? (
                <p className="text-sm text-ink-faint">No attachments.</p>
              ) : (
                <div className="space-y-3">
                  {attachments.map((att) => {
                    const isImage = ['png', 'jpg', 'jpeg', 'bmp'].includes((att.file_ext || '').toLowerCase());
                    return (
                      <div key={att.id} className="flex items-start gap-3 rounded-field border border-paper-border p-3">
                        {isImage && AuthThumb ? (
                          <AuthThumb attachmentId={att.id} alt={att.file_name} />
                        ) : (
                          <div className="flex h-16 w-16 flex-none flex-col items-center justify-center rounded-field border border-paper-border2 bg-paper-muted">
                            <span className="text-xl">📄</span>
                            <span className="text-[10px] font-semibold uppercase text-ink-faint">{att.file_ext}</span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm text-ink" title={att.file_name}>{att.file_name}</div>
                          <div className="mt-0.5 text-xs text-ink-faint">{fmtSize(att.file_size)}{att.uploaded_by_name ? ` · ${att.uploaded_by_name}` : ''}</div>
                          {att.extracted_summary && <p className="mt-1 whitespace-pre-wrap break-words text-xs italic text-ink-faint">{att.extracted_summary}</p>}
                        </div>
                        <button onClick={() => onDownloadAttachment(att)} className="flex-none text-xs text-status-under-process hover:underline">Download</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* ── 11 editable field cards ── */}
            {isAbraaj && (
              <EditField title="Department Assigned" field="department_assigned" value={ticket.department_assigned}
                canEdit={canEdit} kind="select" options={DEPARTMENT_OPTIONS}
                emptyRender={<span className="text-sm italic text-ink-faint">Auto-routed</span>}
                onSave={(v) => onFieldEdit('department_assigned', v)} />
            )}
            <EditField title="Assigned To" field="assigned_to"
              value={String(ticket.assigned_to || '')}
              displayValue={ticket.assigned_to_name || ''}
              canEdit={canReassign}
              emptyRender={<span className="text-sm italic text-ink-faint">Unassigned</span>}
              onSave={(v) => onFieldEdit('assigned_to', v === '' ? null : Number(v))}>
              {({ draft, setDraft }) => (
                <select value={draft} onChange={(e) => setDraft(e.target.value)} className={INPUT}>
                  <option value="">— Unassign —</option>
                  {assignableUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.display_name}{u.department ? ` · ${u.department}` : ''}</option>
                  ))}
                </select>
              )}
            </EditField>
            <EditField title="Order Source" field="order_source" value={ticket.order_source}
              canEdit={canEdit} kind="select" options={ORDER_SOURCE_OPTIONS} onSave={(v) => onFieldEdit('order_source', v)} />
            <EditField title="Customer Segment" field="customer_segment" value={ticket.customer_segment}
              canEdit={canEdit} kind="select" options={CUSTOMER_SEGMENT_OPTIONS} onSave={(v) => onFieldEdit('customer_segment', v)} />
            {isAbraaj && (
              <EditField title="Product Type" field="product_type" value={ticket.product_type}
                canEdit={canEdit} kind="multiselect" options={PRODUCT_TYPE_OPTIONS} onSave={(v) => onFieldEdit('product_type', v)} />
            )}
            {isAbraaj && SKU_APPLICABLE_GROUPS.includes(group) && (
              <>
                <EditField title="Product SKU" field="product_sku" value={ticket.product_sku}
                  canEdit={canEdit} kind="select"
                  onSave={async (v) => {
                    await onFieldEdit('product_sku', v);
                    if (!SKU_IS_PRIVATE_LABEL[v]) await onFieldEdit('product_sku_brand', '');
                  }}>
                  {({ draft, setDraft }) => (
                    <select value={draft} onChange={(e) => setDraft(e.target.value)} className={INPUT}>
                      <option value="">— None —</option>
                      {SKU_OPTIONS.map((cat) => (
                        <optgroup key={cat.category} label={cat.category}>
                          {cat.items.map((it) => (
                            <option key={it.sku} value={it.sku}>{it.sku}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  )}
                </EditField>
                {ticket.product_sku && SKU_IS_PRIVATE_LABEL[ticket.product_sku] && (
                  <EditField title="Brand / Client Name" field="product_sku_brand" value={ticket.product_sku_brand}
                    canEdit={canEdit} placeholder="Client brand name"
                    onSave={(v) => onFieldEdit('product_sku_brand', v)} />
                )}
                <EditField title="Quantity" field="product_quantity" value={ticket.product_quantity}
                  canEdit={canEdit} kind="text"
                  onSave={(v) => onFieldEdit('product_quantity', v === '' ? null : (parseInt(v, 10) || null))}>
                  {({ draft, setDraft }) => (
                    <input type="number" min="0" step="1" value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder="e.g. 5" className={INPUT} />
                  )}
                </EditField>
                <EditField title="Quantity Unit" field="product_quantity_unit" value={ticket.product_quantity_unit}
                  displayValue={QUANTITY_UNIT_LABEL[ticket.product_quantity_unit] || ''}
                  canEdit={canEdit} kind="select"
                  onSave={(v) => onFieldEdit('product_quantity_unit', v || null)}>
                  {({ draft, setDraft }) => (
                    <select value={draft} onChange={(e) => setDraft(e.target.value)} className={INPUT}>
                      <option value="">— None —</option>
                      {QUANTITY_UNIT_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                  )}
                </EditField>
              </>
            )}
            {isAbraaj && SAMPLE_APPLICABLE_GROUPS.includes(group) && (
              <SampleStageStepper
                ticket={ticket}
                currentUser={currentUser}
                onAdvance={onAdvanceSampleStage}
                saving={advancingSampleStage}
              />
            )}
            {isAbraaj && (
              <EditField title="Customer Area" field="customer_area" value={ticket.customer_area}
                canEdit={canEdit} onSave={(v) => onFieldEdit('customer_area', v)}
                emptyRender={<span className="text-sm italic text-ink-faint">Not set</span>}>
                {({ draft, setDraft }) => (
                  <>
                    <input type="text" value={areaSearch || draft} list="kuwait-areas-detail"
                      onChange={(e) => { setAreaSearch(e.target.value); setDraft(e.target.value); }}
                      onBlur={(e) => { const m = KUWAIT_AREAS.find((a) => a.toLowerCase() === e.target.value.toLowerCase()); if (m) { setDraft(m); setAreaSearch(m); } }}
                      placeholder="Type to search area…" className={INPUT} />
                    <datalist id="kuwait-areas-detail">
                      {KUWAIT_AREAS.filter((a) => !areaSearch || a.toLowerCase().includes(areaSearch.toLowerCase())).map((a) => <option key={a} value={a} />)}
                    </datalist>
                  </>
                )}
              </EditField>
            )}
            {isAbraaj && (
              <EditField title="SAP Route" field="sap_route" value={ticket.sap_route}
                canEdit={canEdit} placeholder="e.g. R5G09 or RPETA12" onSave={(v) => onFieldEdit('sap_route', v)} />
            )}
            {group === 'cancel_order' && (
              <EditField title="Cancellation Reason" field="cancellation_reason" value={ticket.cancellation_reason}
                canEdit={canEdit} kind="select" options={CANCELLATION_REASON_OPTIONS} onSave={(v) => onFieldEdit('cancellation_reason', v)}
                emptyRender={<span className="text-sm italic text-status-pending-finance">⚠ Not recorded</span>} />
            )}
            {isAbraaj && ICARRY_APPLICABLE_GROUPS.includes(group) && (
              <EditField title="iCarry Reference" field="icarry_reference" value={ticket.icarry_reference}
                canEdit={canEdit} placeholder="iCarry job / ticket reference" onSave={(v) => onFieldEdit('icarry_reference', v)}
                emptyRender={<span className="text-sm italic text-ink-faint">— Not logged yet</span>} />
            )}
            {(ticket.platform_order_reference || (ticket.order_source && PLATFORM_ORDER_SOURCES.includes(ticket.order_source))) && (
              <EditField title="Platform Order Ref" field="platform_order_reference" value={ticket.platform_order_reference}
                canEdit={canEdit} placeholder="Platform order ID"
                addLabel={ticket.order_source ? `+ Add ${ticket.order_source} order ID` : '+ Add'}
                onSave={(v) => onFieldEdit('platform_order_reference', v)} />
            )}
            {isAbraaj && ['refund_request', 'cancel_order'].includes(group) && (
              <EditField title="Replacement Order" field="replacement_order_reference" value={ticket.replacement_order_reference}
                canEdit={canEdit} placeholder="New SAP order number" addLabel="+ Add KNET replacement order"
                onSave={(v) => onFieldEdit('replacement_order_reference', v)} />
            )}

            {/* Edit Panel */}
            {editing && (
              <Card className="border-status-under-process/30 p-5">
                <div className={`mb-4 ${HEADING}`}>Edit Ticket</div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={LABEL}>Status</label>
                    <select value={editForm.status} onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))} className={INPUT}>
                      {STATUS_FLOW.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>Priority</label>
                    <select value={editForm.complaint_priority} onChange={(e) => setEditForm((p) => ({ ...p, complaint_priority: e.target.value }))} className={INPUT}>
                      {['critical', 'high', 'medium', 'low'].map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>Refund Amount (KD)</label>
                    <input type="number" step="0.001" value={editForm.refund_amount} onChange={(e) => setEditForm((p) => ({ ...p, refund_amount: e.target.value }))} placeholder="0.000" className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>Refund Auth Code</label>
                    <input type="text" value={editForm.refund_auth_code} onChange={(e) => setEditForm((p) => ({ ...p, refund_auth_code: e.target.value }))} placeholder="Authorization code" className={INPUT} />
                  </div>
                </div>
              </Card>
            )}

            {/* Escalate / Change Routing */}
            {canManageTicket && ticket.status !== 'closed' && (
              <Card className="p-5">
                <div className={`mb-4 flex items-center gap-2 ${HEADING}`}><WarningCircle size={16} className="text-status-escalated" />Escalate / Change Routing</div>
                <div className="space-y-3">
                  <div>
                    <label className={LABEL}>Department</label>
                    <select value={escalateForm.department} onChange={(e) => setEscalateForm((p) => ({ ...p, department: e.target.value }))} className={INPUT}>
                      <option value="">Select department…</option>
                      {deptOptions.map((d) => <option key={d.slug} value={d.slug}>{d.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>Reason (optional)</label>
                    <input type="text" value={escalateForm.reason} onChange={(e) => setEscalateForm((p) => ({ ...p, reason: e.target.value }))} placeholder="Reason for routing…" className={INPUT} />
                  </div>
                  <Button variant="primary" disabled={escalating || !escalateForm.department} onClick={() => onEscalate(escalateForm)}>
                    {escalating ? 'Routing…' : 'Route to Department'}
                  </Button>
                </div>
              </Card>
            )}

            {/* Resolve & Finance Gate — 4 sub-states */}
            {showResolvePanel && (
              ticket.status === 'resolved' && ticket.resolution_taken ? (
                <Card className="border-status-resolved/40 bg-status-resolved/8 p-5">
                  <div className="mb-2 flex items-center gap-2 font-serif text-[15px] text-status-resolved"><Check size={16} weight="bold" />Resolved</div>
                  <p className="text-sm text-ink-muted"><b>Resolution:</b> {ticket.resolution_taken}</p>
                </Card>
              ) : isPendingFinance ? (
                <div className="rounded-card border border-status-pending-finance/40 bg-status-pending-finance/8 p-5">
                  <div className="mb-3 flex items-center gap-2 font-serif text-[15px] text-[#a86a0e]"><WarningCircle size={16} className="text-status-pending-finance" />Awaiting Finance Confirmation</div>
                  {canConfirmFinance ? (
                    <Button onClick={onFinanceConfirm} disabled={financeConfirming} className="bg-status-pending-finance hover:bg-status-pending-finance/90">
                      <Check size={14} weight="bold" />{financeConfirming ? 'Confirming…' : 'Finance Confirmed — Move to Resolved'}
                    </Button>
                  ) : (
                    <p className="text-sm italic text-ink-muted">Awaiting Finance team confirmation before this ticket can be closed. <span className="text-ink-faint">(dept_manager / super_admin only)</span></p>
                  )}
                </div>
              ) : isFinanceGroup ? (
                <Card className="p-5">
                  <div className="mb-3 rounded-field border border-status-pending-finance/40 bg-status-pending-finance/8 px-3 py-2.5 text-sm text-[#a86a0e]">
                    ⚠ This ticket requires Finance confirmation before it can be closed. Move it to <b>Pending Finance</b> first.
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className={LABEL}>Responsibility Attribution *</label>
                      <ResponsibilityButtons
                        value={resolveForm.responsibility_attribution}
                        onChange={(v) => setResolveForm((p) => ({ ...p, responsibility_attribution: v }))} />
                    </div>
                    <div>
                      <label className={LABEL}>Resolution Taken *</label>
                      <select value={resolveForm.resolution_taken} onChange={(e) => setResolveForm((p) => ({ ...p, resolution_taken: e.target.value }))} className={INPUT}>
                        <option value="">— Select resolution —</option>
                        {RESOLUTION_TAKEN_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={LABEL}>Notes (optional)</label>
                      <textarea rows={2} value={resolveForm.notes} onChange={(e) => setResolveForm((p) => ({ ...p, notes: e.target.value }))} className={INPUT} placeholder="Additional notes…" dir="auto" />
                    </div>
                    <Button variant="primary"
                      disabled={resolving || !resolveForm.resolution_taken || !resolveForm.responsibility_attribution}
                      onClick={onMoveToPendingFinance}
                      className="bg-status-pending-finance hover:bg-status-pending-finance/90">
                      {resolving ? 'Updating…' : 'Move to Pending Finance'}
                    </Button>
                  </div>
                </Card>
              ) : (
                <Card className="p-5">
                  <div className={`mb-3 flex items-center gap-2 ${HEADING}`}><Check size={16} className="text-status-resolved" />Mark Resolved</div>
                  <div className="space-y-3">
                    <div>
                      <label className={LABEL}>Responsibility Attribution *</label>
                      <ResponsibilityButtons
                        value={resolveForm.responsibility_attribution}
                        onChange={(v) => setResolveForm((p) => ({ ...p, responsibility_attribution: v }))} />
                    </div>
                    <div>
                      <label className={LABEL}>Resolution Taken *</label>
                      <select value={resolveForm.resolution_taken} onChange={(e) => setResolveForm((p) => ({ ...p, resolution_taken: e.target.value }))} className={INPUT}>
                        <option value="">— Select resolution —</option>
                        {RESOLUTION_TAKEN_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={LABEL}>Notes (optional)</label>
                      <textarea rows={3} value={resolveForm.notes} onChange={(e) => setResolveForm((p) => ({ ...p, notes: e.target.value }))} className={INPUT} placeholder="Summary of resolution…" dir="auto" />
                    </div>
                    <Button variant="primary"
                      disabled={resolving || !resolveForm.resolution_taken || !resolveForm.responsibility_attribution}
                      onClick={() => onResolve(resolveForm)}>
                      {resolving ? 'Resolving…' : 'Mark Resolved'}
                    </Button>
                  </div>
                </Card>
              )
            )}

            {/* Terminal Summary — read-only Resolution + editable Responsibility for resolved/closed tickets */}
            {['resolved', 'closed'].includes(ticket.status) && (
              <Card className="border-status-resolved/40 bg-status-resolved/8 p-5">
                <div className="mb-2 flex items-center gap-2 font-serif text-[15px] text-status-resolved">
                  <Check size={16} weight="bold" />
                  {ticket.status === 'closed' ? 'Closed' : 'Resolved'}
                </div>
                {ticket.resolution_taken && (
                  <p className="mb-2 text-sm text-ink-muted"><b>Resolution:</b> {ticket.resolution_taken}</p>
                )}
                <div className="flex items-center gap-2 text-sm text-ink-muted">
                  <b>Responsibility:</b>
                  {editingAttribution ? (
                    <div className="flex flex-1 items-center gap-2">
                      <div className="flex-1">
                        <ResponsibilityButtons value={attributionDraft} onChange={setAttributionDraft} />
                      </div>
                      <Button variant="primary"
                        disabled={!attributionDraft || attributionDraft === ticket.responsibility_attribution}
                        onClick={async () => {
                          await onAttributionEdit(attributionDraft);
                          setEditingAttribution(false);
                        }}>
                        Save
                      </Button>
                      <button
                        onClick={() => setEditingAttribution(false)}
                        className="text-xs text-ink-muted hover:text-ink">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="inline-flex items-center rounded-chip bg-paper-muted px-2 py-0.5 text-xs font-medium text-ink">
                        {RESPONSIBILITY_ATTRIBUTION_LABEL[ticket.responsibility_attribution] || 'Not set'}
                      </span>
                      {canManageTicket && canEditAttribution && (
                        <button
                          onClick={() => {
                            setAttributionDraft(ticket.responsibility_attribution || '');
                            setEditingAttribution(true);
                          }}
                          className="text-xs text-status-under-process hover:underline">
                          Edit
                        </button>
                      )}
                    </>
                  )}
                </div>
              </Card>
            )}

            {/* Close Ticket */}
            {canManageTicket && (ticket.status === 'resolved' || ticket.status === 'pending_customer_contact') && (
              <Card className="p-5">
                <div className={`mb-3 flex items-center gap-2 ${HEADING}`}><Check size={16} className="text-status-resolved" />Close Ticket</div>
                <div className="mb-4 rounded-field border border-status-under-process/30 bg-status-under-process/8 px-3 py-2.5 text-sm text-ink-muted">
                  {ticket.status === 'resolved' ? 'Ticket has been marked resolved. Confirm customer contact details to close.' : 'Department has confirmed their action. Contact the customer before closing.'}
                </div>
                <div className="space-y-3">
                  <label className="flex items-center gap-2.5 text-sm text-ink-muted">
                    <input type="checkbox" checked={closureForm.customer_contacted} onChange={(e) => setClosureForm((p) => ({ ...p, customer_contacted: e.target.checked }))} className="h-4 w-4 rounded" />
                    Customer was contacted
                  </label>
                  {closureForm.customer_contacted && (
                    <div>
                      <label className={LABEL}>Contact Method</label>
                      <select value={closureForm.contact_method} onChange={(e) => setClosureForm((p) => ({ ...p, contact_method: e.target.value }))} className={INPUT}>
                        {['WhatsApp', 'Phone', 'Email', 'Not contacted'].map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className={LABEL}>Closing Notes *</label>
                    <textarea rows={3} value={closureForm.closing_notes} onChange={(e) => setClosureForm((p) => ({ ...p, closing_notes: e.target.value }))} className={INPUT} placeholder="Summary of resolution…" dir="auto" />
                  </div>
                  <Button variant="primary" disabled={closing || !closureForm.customer_contacted}
                    title={!closureForm.customer_contacted ? 'Confirm the customer was contacted before closing' : undefined}
                    onClick={() => onClose(closureForm)}>
                    {closing ? 'Closing…' : 'Close Ticket'}
                  </Button>
                </div>
              </Card>
            )}

            {/* Reopen */}
            {canManageTicket && (ticket.status === 'closed' || ticket.status === 'resolved') && (
              <Card className="p-5">
                <div className={`mb-3 ${HEADING}`}>Reopen Ticket</div>
                <div className="space-y-3">
                  <div>
                    <label className={LABEL}>Reason *</label>
                    <input type="text" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} className={INPUT} placeholder="Why is this ticket being reopened?" />
                  </div>
                  <Button variant="primary" disabled={reopening || !reopenReason.trim()} onClick={() => onReopen(reopenReason)}>
                    {reopening ? 'Reopening…' : 'Reopen Ticket'}
                  </Button>
                </div>
              </Card>
            )}

            {/* Third Party Submissions */}
            {thirdPartySubmissions.length > 0 && (
              <Card className="p-5">
                <div className={`mb-4 ${HEADING}`}>Third Party Submissions</div>
                <div className="space-y-3">
                  {thirdPartySubmissions.map((sub) => (
                    <div key={sub.id} className="rounded-field bg-paper-muted p-4">
                      <div className="mb-2 flex items-start justify-between">
                        <span className="text-sm font-semibold capitalize text-ink">{sub.submission_type} Report</span>
                        <span className="text-xs text-ink-faint">{fmtDate(sub.created_at)}</span>
                      </div>
                      <div className="mb-1 text-xs text-ink-faint">{sub.submitted_by_email}</div>
                      <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded bg-paper p-2 text-xs text-ink-muted">{JSON.stringify(sub.data, null, 2)}</pre>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Department Actions + dept response form */}
            <Card className="p-5">
              <div className={`mb-4 ${HEADING}`}>Department Actions</div>
              {deptActions.length === 0 && <p className="py-3 text-center text-sm text-ink-faint">No department responses yet.</p>}
              <div className="mb-4 space-y-3">
                {deptActions.map((da) => (
                  <div key={da.id} className="rounded-field bg-paper-muted p-3">
                    <div className="mb-1 flex items-start justify-between">
                      <span className="text-sm font-bold text-ink">{da.department_slug}</span>
                      {da.confirmed_done && <span className="inline-flex items-center gap-1 rounded-chip bg-status-resolved/12 px-2 py-0.5 text-xs font-medium text-status-resolved"><Check size={11} weight="bold" />Confirmed</span>}
                    </div>
                    {da.dept_user_name && <p className="mb-1 text-xs text-ink-faint">By: {da.dept_user_name}</p>}
                    {da.response && <p className="mb-1 text-sm text-ink-muted" dir="auto">{da.response}</p>}
                    {da.action_taken && <p className="text-xs text-ink-faint">Action: {da.action_taken}</p>}
                    {da.confirmed_at && <p className="mt-1 text-xs text-ink-faint">Confirmed: {fmtDate(da.confirmed_at)}</p>}
                  </div>
                ))}
              </div>
              {isDeptUser && canRespondAsDept && (
                <div className="space-y-3 border-t border-paper-border pt-4">
                  <h4 className="text-sm font-semibold text-ink">Submit Department Response</h4>
                  <div>
                    <label className={LABEL}>Response *</label>
                    <textarea rows={3} value={deptResponse.response} onChange={(e) => setDeptResponse((p) => ({ ...p, response: e.target.value }))} className={INPUT} placeholder="Describe what was done…" dir="auto" />
                  </div>
                  <div>
                    <label className={LABEL}>Action Taken</label>
                    <input type="text" value={deptResponse.action_taken} onChange={(e) => setDeptResponse((p) => ({ ...p, action_taken: e.target.value }))} className={INPUT} placeholder="Specific action taken…" />
                  </div>
                  <div>
                    <label className={LABEL}>Attachments (optional)</label>
                    <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => setDeptFiles(Array.from(e.target.files || []))} className="text-xs text-ink-muted" />
                    {deptFiles.length > 0 && <div className="mt-1 text-xs text-ink-faint">{deptFiles.length} file(s) selected</div>}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-ink-muted">
                    <input type="checkbox" checked={deptResponse.confirmed_done} onChange={(e) => setDeptResponse((p) => ({ ...p, confirmed_done: e.target.checked }))} className="h-4 w-4 rounded" />
                    Mark as action complete
                  </label>
                  <Button variant="primary" disabled={submittingDeptResponse || !deptResponse.response.trim()} onClick={submitDept}>
                    {submittingDeptResponse ? 'Submitting…' : 'Submit Response'}
                  </Button>
                </div>
              )}
            </Card>

            {/* Sales Call Outcome */}
            {group === 'sales_call_request' && (
              <Card className="p-5">
                <div className={`mb-4 flex items-center gap-2 ${HEADING}`}>📞 Sales Call Outcome</div>
                <div className="mb-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                  {SALES_OUTCOMES.map((o) => (
                    <button key={o.key} onClick={() => setSalesOutcome((prev) => prev === o.key ? null : o.key)}
                      className={`rounded-field px-2.5 py-2.5 text-center text-[13px] transition ${salesOutcome === o.key ? 'border-[1.5px] border-status-resolved bg-status-resolved/8 font-semibold text-status-resolved' : 'border border-paper-border2 text-ink hover:bg-paper-muted'}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
                {salesOutcome && (
                  <div className="space-y-3 border-t border-paper-border pt-4">
                    <div>
                      <label className={LABEL}>Additional notes (optional)</label>
                      <textarea rows={3} value={salesNotes} onChange={(e) => setSalesNotes(e.target.value)} className={INPUT} placeholder="Any additional context about this call…" dir="auto" />
                    </div>
                    <div className="flex items-center gap-3">
                      <Button variant="primary" disabled={addingNote} onClick={submitSales}>{addingNote ? 'Submitting…' : 'Submit Outcome'}</Button>
                      <button onClick={() => { setSalesOutcome(null); setSalesNotes(''); }} className="text-sm text-ink-muted hover:text-ink">Cancel</button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* RIGHT column */}
          <div className="flex flex-col gap-6 p-7">
            {/* Customer */}
            <section>
              <div className={`mb-3.5 ${HEADING}`}>Customer</div>
              <div className="flex flex-col gap-2.5 text-[13px]">
                <Row k="Name" v={ticket.customer_name || hubspotEnrichment?.contactName || '—'} />
                <Row k="Mobile" v={ticket.customer_mobile || '—'} />
                {enrichmentLoading && !ticket.customer_email && !hubspotEnrichment?.contactEmail ? (
                  <div className="flex justify-between"><span className="text-ink-faint">Email</span><span className="h-3 w-32 animate-pulse rounded bg-paper-muted" /></div>
                ) : (
                  <Row k="Email" v={ticket.customer_email || hubspotEnrichment?.contactEmail || '—'} />
                )}
                {ticket.hubspot_ticket_id && (
                  <div className="flex justify-between"><span className="text-ink-faint">HubSpot</span><span className="text-ink-muted">{ticket.hubspot_ticket_id}</span></div>
                )}
              </div>
              {hubspotEnrichment?.deals?.length > 0 && (
                <div className="mt-4 border-t border-paper-border pt-3.5">
                  <div className="mb-2 text-xs text-ink-faint">Order history (HubSpot deals)</div>
                  {hubspotEnrichment.deals.map((d, i) => (
                    <div key={i} className="flex justify-between py-1.5 text-[13px]"><span className="text-ink">{d.name}</span><span className="text-ink-muted">{d.value}</span></div>
                  ))}
                </div>
              )}
            </section>

            {/* Assignment */}
            <section>
              <div className={`mb-3.5 ${HEADING}`}>Assignment</div>
              <div className="flex flex-col gap-2.5 text-[13px]">
                <Row k="Assigned To" v={ticket.assigned_to_name || 'Unassigned'} />
                {ticket.refund_amount && <Row k="Refund Amount" v={`KD ${parseFloat(ticket.refund_amount).toFixed(3)}`} />}
                {ticket.refund_auth_code && <Row k="Auth Code" v={ticket.refund_auth_code} />}
                {ticket.hubspot_ticket_id && <Row k="HubSpot ID" v={ticket.hubspot_ticket_id} />}
              </div>
            </section>

            {/* Status History */}
            <section>
              <div className={`mb-3.5 ${HEADING}`}>Status History</div>
              <ol className="flex flex-col">
                {(statusHistory || []).map((h, i) => (
                  <li key={h.id || i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className={`mt-1 h-2.5 w-2.5 rounded-full ${STATUS_DOT[h.new_status] || 'bg-ink-faint'}`} />
                      {i < statusHistory.length - 1 && <span className="w-0.5 flex-1 bg-paper-border2" />}
                    </div>
                    <div className="pb-4">
                      <div className="text-[13px] font-semibold text-ink">
                        {h.old_status ? (h.old_status === h.new_status ? <span className="italic text-ink-faint">Routing note</span> : <>{getStatusLabel(h.old_status)} → {getStatusLabel(h.new_status)}</>) : <>Ticket created ({getStatusLabel(h.new_status)})</>}
                      </div>
                      <div className="text-xs text-ink-faint">by {h.changed_by_name || 'System'} · {fmtDate(h.created_at)}</div>
                      {h.note && <div className="mt-0.5 text-xs text-ink-muted" dir="auto">{h.note}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* Notes & Activity */}
            <section>
              <div className={`mb-3.5 ${HEADING}`}>Notes &amp; Activity ({notes.length})</div>
              <div className="mb-4 max-h-96 space-y-3 overflow-y-auto">
                {notes.length === 0 && <p className="py-4 text-center text-sm text-ink-faint">No notes yet</p>}
                {notes.map((note) => (
                  <div key={note.id} className="rounded-lg border border-paper-border bg-paper-panel p-3 text-[13px] shadow-soft">
                    <div className="mb-1 flex items-start justify-between">
                      <span className="font-semibold text-ink">{note.user_name || 'System'}<span className="ml-1.5 font-normal capitalize text-ink-faint">({note.user_role?.replace('_', ' ')})</span></span>
                      <span className="text-xs text-ink-faint">{fmtDate(note.created_at)}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-ink-muted" dir="auto">{note.note}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note…" dir="auto"
                  onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey && noteText.trim()) { onAddNote(noteText); setNoteText(''); } }}
                  className={INPUT} />
                <Button variant="primary" disabled={!noteText.trim() || addingNote} onClick={() => { onAddNote(noteText); setNoteText(''); }}>Send</Button>
              </div>
              <p className="mt-1 text-xs text-ink-faint">Ctrl+Enter to submit</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
